/**
 * Tradução de um documento já existente no ERP (`CheckoutFaturarNFCe`) para o
 * modelo de carrinho do Checkout (T003,
 * `specs/006-importacao-dav/contracts/importacao-domain-api.md` §1).
 *
 * Domínio **puro**: sem rede, sem Zustand, sem React. Mora em
 * `domain/importacaoVenda/`, e não em `domain/precificacao/`, porque não é
 * lógica de preço — é tradução de shape de API. Nada aqui soma, desconta ou
 * arredonda: os valores chegam já resolvidos pelo ERP, em centavos inteiros
 * convertidos na fronteira Zod, e são copiados como estão (Constitution V).
 *
 * É reaproveitado pela feature 011 (recuperação de rascunho de NFCe): a única
 * diferença entre as duas features é qual endpoint alimenta
 * `CheckoutFaturarNFCe` — `GetDav` aqui, `CarregarNFCe` lá. Por isso
 * `mapearVendaExistente` não conhece a palavra "DAV" em lugar nenhum: a origem
 * aparece só em `paraLinhaCarrinho`, na conversão para linha de carrinho.
 *
 * A 011 chegou (AD-166) e `paraLinhaCarrinho` recebeu o parâmetro `origem` que
 * a ressalva anterior deste comentário previa: o grau de liberdade deixou de
 * ser especulativo no instante em que passou a existir um segundo call site.
 */

import type { DadosTEF } from '../pagamento/saldoPagamento';
import type { Centavos } from '../precificacao/dinheiro';
import { ZERO_CENTAVOS } from '../precificacao/dinheiro';
import type { LinhaCarrinho } from '../precificacao/linha';
import type { Milesimos } from '../precificacao/quantidade';
import { ZERO_MILESIMOS } from '../precificacao/quantidade';
import type { CheckoutFaturarNFCe } from '../../../shared/schemas/dav.schema';

/** Contrato violado pela resposta do ERP — não é dado de negócio ausente. */
export class ErroDocumentoImportadoInvalido extends Error {
  constructor(campo: string) {
    super(
      `Documento importado sem \`${campo}\`: o ERP devolveu uma resposta que não permite iniciar a venda.`,
    );
    this.name = 'ErroDocumentoImportadoInvalido';
  }
}

export interface LinhaImportada {
  readonly codigoProduto: string;
  /**
   * `null` até a resolução best-effort por `GetProduto` (AD-096) — o documento
   * não traz descrição. Nunca bloqueia a importação: a linha entra no carrinho
   * com o código no lugar do nome e é atualizada depois, se a chamada vier.
   */
  readonly descricao: string | null;
  readonly quantidade: Milesimos;
  /** Congelado: nunca passa por `resolvePrecoUnitario` (AD-067, `FR-006`). */
  readonly precoUnitario: Centavos;
  /** Absoluto sobre o total da linha, já resolvido pelo ERP. */
  readonly descontoLinha: Centavos;
  readonly udm: string;
}

/**
 * TEF de um documento importado, já na forma que o pagamento guarda.
 *
 * É o próprio `DadosTEF` desde AD-259: o que importa do documento é o
 * `TEFPagId`, que precisa ser **reenviado** ao faturar para o ERP não perder o
 * vínculo com a `TransacaoTEF`. NSU e autorização não vêm no documento e ficam
 * vazios — eles só existem na tela da aprovação, que aqui não houve.
 */
export type TefImportado = DadosTEF;

export interface FormaPagamentoImportada {
  readonly formaCodigo: number;
  readonly formaMeioPagtoNFe: string;
  readonly valor: Centavos;
  /** `null` quando o item não é TEF — o agrupamento evita cinco campos soltos. */
  readonly tef: TefImportado | null;
  readonly pixGuid: string | null;
  readonly ticketDevolucao: string | null;
}

export interface VendaImportada {
  /**
   * `NumeroRascunho` do documento, reenviado **intacto** em `FaturarNFCe`
   * (NFCE-02, AD-235). Único elo com o documento de origem desde a remoção de
   * `DavNum` (AD-107): é por este rascunho que o ERP reconhece a origem e fecha
   * o DAV sozinho ao faturar (AD-058).
   */
  readonly numeroRascunho: number;
  /**
   * `CadSerieNFCe` do documento (`R01` no preview), reenviado junto do número
   * (AD-239) — ou `''` quando o ERP não a informa, e aí o retrato cai na série
   * da sessão.
   */
  readonly serie: string;
  /** Sempre sobrescreve o cliente atual da venda (`FR-007`). */
  readonly clienteCodigo: number;
  /**
   * `ClienteNome` do documento (contrato de 2026-09-14, AD-237), ou `null`
   * quando ausente/em branco.
   *
   * Não substitui o `GetCliente`, que continua trazendo lista de preço,
   * convênio e celular: é o que permite importar mesmo quando essa chamada
   * falha, exibindo o cliente pelo nome que o próprio documento dá.
   */
  readonly clienteNome: string | null;
  /**
   * Sempre sobrescreve o vendedor atual da venda (`FR-007`).
   *
   * O do documento, ou — quando o ERP o devolve `0` (divergência registrada em
   * `PENDENCIES.md`, AD-235) — o código da linha da listagem de origem.
   */
  readonly vendedorCodigo: number;
  /**
   * Nome do vendedor, ou `null` quando nenhuma das duas fontes o tem.
   *
   * Duas fontes possíveis, nesta ordem de prioridade (AD-172): o **documento**
   * (`CheckoutFaturarNFCe.vendedorNome`, campo novo no SDT — `GetDav` e
   * `CarregarNFCe` devolvem o mesmo SDT, então serve às duas features) e, na
   * ausência dele, a **listagem de origem** (`VendedorDaLista.nome` —
   * `GetListaNFCes` traz `VendedorNome`; `ListaDAVs` também, mas o ERP de
   * 2026-09-14 ainda o devolve vazio). O documento é preferido por ser a mesma
   * fonte para as duas features; a listagem cobre o vazio que o ERP de
   * 2026-09-14 ainda devolve em `CarregarNFCe`/`GetDav` (AD-235).
   *
   * `null`, e nunca `''`: string vazia é o default do SDT GeneXus para campo
   * não preenchido, e propagá-la faria a UI exibir um vendedor sem nome em vez
   * de cair no código.
   */
  readonly vendedorNome: string | null;
  readonly linhas: readonly LinhaImportada[];
  readonly formasDePagamento: readonly FormaPagamentoImportada[];
  /**
   * `CondicaoPagamentoCodigo` do documento — **uma venda por condição** (I1 da
   * 008), então o escalar do documento é o escalar da venda retomada.
   *
   * `0` significa "o documento não tem condição escolhida": um rascunho pode ter
   * sido suspenso antes de o operador chegar ao pagamento. Nesse caso a venda
   * retomada segue sem condição e o operador escolhe normalmente — não é dado
   * ausente a suprir, é o estado real do documento.
   *
   * Era **descartado** até 2026-09-08 (AD-171): o mapeador nunca leu o campo,
   * embora `dav.schema.ts` já o validasse. Com forma de pagamento importada,
   * `selecionarCondicao` passava a recusar toda escolha (`pagamentos.length > 0`)
   * e `montarPagamentosParaPayload` enviava `CondicaoPagamentoCodigo: 0` ao
   * `FaturarNFCe` — que o ERP real recusa com "Condição de Pagamento 0 não
   * Localizada" (AD-165).
   */
  readonly condicaoPagamentoCodigo: number;
}

/** Campo de texto vazio no ERP significa "não informado", não string vazia. */
function ouNulo(valor: string): string | null {
  return valor === '' ? null : valor;
}

/**
 * Um item de pagamento só é TEF quando o ERP preencheu o `TEFPagId`.
 *
 * **Até AD-259 o critério era `TEFidentificacao !== 0`**, campo que o SDT
 * `CheckoutFaturarNFCe` da KB não tem mais. Hoje vale o `TEFPagId` não vazio —
 * `''` é o default do SDT GeneXus para item não-TEF. Um documento do ERP
 * anterior, sem `TEFPagId`, tem os campos antigos ignorados: sem o vínculo, não
 * há o que reenviar, e montar um `TefImportado` de campos em branco faria a
 * feature 008 tratar como TEF um item que o ERP novo não reconheceria.
 *
 * A forma importada continua entrando com `integracao: 'NENHUMA'` — torná-la
 * estornável pelo Checkout é o item 65 de `PENDENCIES.md`.
 */
function paraTef(item: CheckoutFaturarNFCe['FormasDePagamento'][number]): TefImportado | null {
  const pagId = item.TEFPagId?.trim() ?? '';
  if (pagId === '') {
    return null;
  }
  return {
    pagId,
    bandeira: item.TEFBandeira,
    nsu: '',
    autorizacao: '',
    tipoIntegracao: '1',
  };
}

/**
 * Vendedor capturado na linha da listagem de origem — `codigo`/`nome` `null`
 * quando a listagem não os traz ou a origem não veio de uma lista.
 */
export interface VendedorDaLista {
  readonly codigo: number | null;
  readonly nome: string | null;
}

const SEM_VENDEDOR_DA_LISTA: VendedorDaLista = { codigo: null, nome: null };

/**
 * @param vendedorDaLista Vendedor da linha da listagem, usado só como
 * *fallback*:
 * - `nome`, atrás de `vendedorNome` do documento (AD-172);
 * - `codigo`, quando o documento traz `vendedorCodigo` **0** — o ERP de
 *   2026-09-14 devolve 0 em `CarregarNFCe`/`GetDav` mesmo com vendedor gravado
 *   (divergência registrada em `PENDENCIES.md`, AD-235). O documento continua
 *   tendo prioridade sempre que traz um código.
 *
 * Nunca lança por dado de negócio ausente (documento sem forma de pagamento,
 * sem produto): devolve arrays vazios. Lança **só** por violação de contrato —
 * `clienteCodigo`, `vendedorCodigo` ou `NumeroRascunho` ausentes.
 *
 * O nome do cliente vem do **documento** (`ClienteNome`, AD-237), nunca da
 * linha da listagem. O cadastro completo continua sendo resolvido por
 * `resolverCliente` (`GetCliente` por `CodCliente`, AD-115); o nome do
 * documento é o recuo quando essa chamada falha. (Um `clienteNome` capturado da
 * **listagem** existiu aqui até 2026-09-08 e foi removido como campo morto por
 * AD-173; o de agora tem outra fonte e um consumidor.)
 */
export function mapearVendaExistente(
  resposta: CheckoutFaturarNFCe,
  vendedorDaLista: VendedorDaLista = SEM_VENDEDOR_DA_LISTA,
): VendaImportada {
  // Reforço em runtime da invariante que o schema Zod já expressa em tipo. A
  // entrada pode chegar de um caller não totalmente tipado (a resposta crua do
  // ERP, um teste): sem esta checagem, um `NumeroRascunho` ausente viraria
  // `undefined` no payload de `FaturarNFCe` e o DAV nunca fecharia no ERP —
  // falha silenciosa, detectável só na conferência fiscal (D8/AD-107).
  if (typeof resposta.NumeroRascunho !== 'number') {
    throw new ErroDocumentoImportadoInvalido('NumeroRascunho');
  }
  if (typeof resposta.clienteCodigo !== 'number') {
    throw new ErroDocumentoImportadoInvalido('clienteCodigo');
  }
  if (typeof resposta.vendedorCodigo !== 'number') {
    throw new ErroDocumentoImportadoInvalido('vendedorCodigo');
  }

  return {
    // Ausente vira `0`, e não exceção: ao contrário dos três acima, a condição
    // tem um "não informado" legítimo (documento suspenso antes do pagamento),
    // e `0` é exatamente como o próprio ERP o representa.
    condicaoPagamentoCodigo:
      typeof resposta.CondicaoPagamentoCodigo === 'number' ? resposta.CondicaoPagamentoCodigo : 0,
    numeroRascunho: resposta.NumeroRascunho,
    serie: (resposta.CadSerieNFCe ?? '').trim(),
    clienteCodigo: resposta.clienteCodigo,
    clienteNome: ouNulo((resposta.ClienteNome ?? '').trim()),
    // `0` é o "sem vendedor" do SDT — e, no ERP de 2026-09-14, também o que
    // `CarregarNFCe`/`GetDav` devolvem por engano com vendedor gravado. Cai no
    // código da listagem quando houver; sem ele, continua `0`, como antes.
    vendedorCodigo:
      resposta.vendedorCodigo === 0 && vendedorDaLista.codigo !== null
        ? vendedorDaLista.codigo
        : resposta.vendedorCodigo,
    // Documento primeiro (AD-172): ausente enquanto o deploy do ERP não sai,
    // `?? ''` cai em `ouNulo` e vira `null` — e o `??` seguinte cai para o
    // nome capturado da listagem, quando houver. Nome em branco em qualquer
    // uma das duas fontes é "não informado", não string vazia: um `''`
    // chegaria ao slice de vendedor e a UI exibiria um vendedor sem nome em
    // vez de cair no comportamento de "só o código".
    vendedorNome: ouNulo(resposta.vendedorNome ?? '') ?? ouNulo(vendedorDaLista.nome ?? ''),
    linhas: resposta.produtos.map((produto) => ({
      // Sem os espaços do `char` do ERP (`"50153         "`, medido em
      // 2026-09-16): com eles o `GetProduto` da descrição não achava o produto e
      // a linha ficava com o código no lugar do nome (AD-239).
      codigoProduto: produto.codigoProduto.trim(),
      descricao: null,
      quantidade: produto.quantidade,
      precoUnitario: produto.precoUnitario,
      descontoLinha: produto.DescontoValor,
      udm: produto.UDM,
    })),
    formasDePagamento: resposta.FormasDePagamento.map((item) => ({
      formaCodigo: item.FormaCodigo,
      formaMeioPagtoNFe: item.FormaMeioPagtoNFe,
      valor: item.FormaValor,
      tef: paraTef(item),
      pixGuid: ouNulo(item.FormaPixGUID),
      ticketDevolucao: ouNulo(item.TicketDevolucao),
    })),
  };
}

/**
 * Origem de uma linha nascida de um documento já existente no ERP.
 *
 * As duas features que importam documento (`GetDav`/006 e `CarregarNFCe`/011)
 * produzem linhas idênticas em tudo — congeladas, sem reprecificação — menos
 * neste rótulo, que é o que a grid e a auditoria leem para dizer de onde a
 * venda veio.
 */
export type OrigemDocumentoImportado = 'DAV' | 'RASCUNHO';

/**
 * Converte uma linha importada em `LinhaCarrinho` (`data-model.md` §3).
 *
 * `idLinha` é **parâmetro**, não gerado aqui: o domínio puro não conhece
 * `crypto`, e o `CarrinhoSlice` já expõe um gerador injetável (`gerarIdLinha`)
 * que é o que torna o id determinístico em teste. `origem` é parâmetro pela
 * razão oposta: é a **única** coisa que distingue uma linha de DAV (006) de uma
 * linha de rascunho de NFCe (011), e fixá-la aqui obrigaria a duplicar a função
 * inteira para mudar um literal (AD-166).
 *
 * O desconto do documento entra em `descontoManual`, não em `descontoConvenio`.
 * `descontoConvenio` é campo **derivado** — `repricarSku` o reescreve a cada
 * reprecificação (`linha.ts`). Enquanto congelada a linha fica fora do
 * agregado e nada a reescreveria; mas basta o operador editá-la para ela
 * descongelar (invariante I6), e aí a primeira reprecificação apagaria o
 * desconto que veio do documento. Em `descontoManual` ele sobrevive, que é o
 * comportamento correto: é um desconto já concedido e registrado no ERP, não um
 * convênio a recalcular sob o cliente atual.
 */
export function paraLinhaCarrinho(
  linha: LinhaImportada,
  idLinha: string,
  origem: OrigemDocumentoImportado,
): LinhaCarrinho {
  return {
    idLinha,
    snapshot: {
      codigoProduto: linha.codigoProduto,
      // Fallback: o código no lugar do nome, nunca string vazia — a linha
      // precisa ser identificável na grid antes de `GetProduto` responder.
      descricao: linha.descricao ?? linha.codigoProduto,
      unidadeMedida: linha.udm,
      precoBase: linha.precoUnitario,
      // `TipoPreco = 8` não se aplica a linha congelada: ela nunca entra em
      // `repricarSku`, então estas faixas jamais são lidas (invariante I3).
      precosFaixa: [
        ZERO_CENTAVOS,
        ZERO_CENTAVOS,
        ZERO_CENTAVOS,
        ZERO_CENTAVOS,
        ZERO_CENTAVOS,
      ] as const,
      limiaresFaixa: [ZERO_MILESIMOS, ZERO_MILESIMOS, ZERO_MILESIMOS, ZERO_MILESIMOS] as const,
      // Linha congelada não reabre edição de pesagem.
      pesavelEditavel: '',
      // O documento já foi aceito pelo ERP: a linha importada não entra no
      // limite do preço mínimo (017, `research.md` D2).
      precoMinimo: ZERO_CENTAVOS,
    },
    quantidade: linha.quantidade,
    precoUnitario: linha.precoUnitario,
    descontoConvenio: ZERO_CENTAVOS,
    descontoManual: linha.descontoLinha,
    cancelada: false,
    // Invariante I5: derivado da origem, nunca informado pelo call site.
    precoCongelado: true,
    origem,
  };
}
